import { useEffect, useMemo, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/app/core/auth/AuthProvider'

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

export default function SignIn() {
  const { isAuthenticated, isLoading, login } = useAuth()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  // Invitation emails link to "…/?login_hint=<email>". AuthGuard forwards that target
  // (query string included) in state.from. Pull the hint out so we can send invitees
  // straight to B2C — the sign-up/first-sign-in there is what activates their account.
  const loginHint = useMemo(() => {
    const q = from.includes('?') ? from.slice(from.indexOf('?')) : location.search
    return new URLSearchParams(q).get('login_hint') ?? undefined
  }, [from, location.search])

  // Auto-launch the B2C redirect (email prefilled) for invite links so the invitee is
  // never parked behind this login wall. Fires once; the button stays as a fallback.
  const autoTriggered = useRef(false)
  useEffect(() => {
    if (loginHint && !isAuthenticated && !isLoading && !autoTriggered.current) {
      autoTriggered.current = true
      login(loginHint)
    }
  }, [loginHint, isAuthenticated, isLoading, login])

  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  const redirecting = Boolean(loginHint) || isLoading

  return (
    <div className="min-h-screen flex">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B1C5B] text-white flex-col items-center justify-center px-12">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold tracking-tight">xLongevity</h1>
          <p className="text-xl font-medium text-white/90">Ops</p>
          <p className="text-sm text-white/70 max-w-xs mx-auto leading-relaxed">
            Streamline your operations with powerful tools for managing inventory,
            procurement, suppliers, and fulfilment.
          </p>
        </div>
      </div>

      {/* Action panel */}
      <div className="flex w-full lg:w-1/2 items-center justify-center bg-white px-6">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground">
              {loginHint ? 'Activate your account' : 'Welcome back'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {loginHint
                ? 'Redirecting you to secure sign-in to finish setting up your account…'
                : 'Sign in to xLongevity Ops'}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11"
            onClick={() => login(loginHint)}
            disabled={redirecting}
          >
            {redirecting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
              </>
            ) : (
              <>
                <MicrosoftLogo /> Sign in with Microsoft
              </>
            )}
          </Button>

          <p className="mt-6 text-center text-xs text-muted-foreground leading-relaxed">
            You will be redirected to your organization&apos;s secure sign-in.
            Access and roles are managed in the Admin Portal.
          </p>
        </div>
      </div>
    </div>
  )
}
